import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, useRef, useEffect } from 'react';
import { Upload, Database, UserPlus, CheckCircle, AlertCircle } from 'lucide-react';
import { Card, CardBody, CardHeader, Button, Input } from '@/components/ui';

export const Route = createFileRoute('/setup')({
  component: SetupPage,
});

function SetupPage() {
  const navigate = useNavigate();
  const [isChecking, setIsChecking] = useState(true);
  const [needsSetup, setNeedsSetup] = useState(false);
  const [activeTab, setActiveTab] = useState<'restore' | 'create'>('restore');

  // Restore state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isRestoring, setIsRestoring] = useState(false);
  const [restoreError, setRestoreError] = useState<string | null>(null);
  const [restoreSuccess, setRestoreSuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Create admin state
  const [adminForm, setAdminForm] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
  });
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createSuccess, setCreateSuccess] = useState(false);

  const checkSetupStatus = async () => {
    try {
      const response = await fetch('/api/setup/status');
      const data = await response.json();

      if (!data.needsSetup) {
        // Database already has data, redirect to login
        navigate({ to: '/login' });
        return;
      }

      setNeedsSetup(true);
    } catch (error) {
      console.error('Failed to check setup status:', error);
      setNeedsSetup(true); // Assume setup needed if check fails
    } finally {
      setIsChecking(false);
    }
  };

  // Check if setup is needed on mount.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- checkSetupStatus is stable; wrapping in useCallback would obscure this one-time init pattern
  useEffect(() => {
    checkSetupStatus();
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.endsWith('.sql') && !file.name.endsWith('.dump')) {
        setRestoreError('Only .sql and .dump files are allowed');
        return;
      }
      setSelectedFile(file);
      setRestoreError(null);
    }
  };

  const handleRestore = async () => {
    if (!selectedFile) return;

    setIsRestoring(true);
    setRestoreError(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await fetch('/api/setup/restore', {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error?.message || data.message || 'Restore failed');
      }

      setRestoreSuccess(true);

      // Force a full page reload to reset all app state and redirect to login
      setTimeout(() => {
        window.location.href = '/login';
      }, 2000);
    } catch (error) {
      setRestoreError(error instanceof Error ? error.message : 'Restore failed');
    } finally {
      setIsRestoring(false);
    }
  };

  const handleCreateAdmin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (adminForm.password !== adminForm.confirmPassword) {
      setCreateError('Passwords do not match');
      return;
    }

    if (adminForm.password.length < 8) {
      setCreateError('Password must be at least 8 characters');
      return;
    }

    setIsCreating(true);
    setCreateError(null);

    try {
      const response = await fetch('/api/setup/create-admin', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: adminForm.email,
          password: adminForm.password,
          firstName: adminForm.firstName,
          lastName: adminForm.lastName,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error?.message || data.message || 'Failed to create admin');
      }

      setCreateSuccess(true);

      // Force a full page reload to reset all app state and redirect to login
      setTimeout(() => {
        window.location.href = '/login';
      }, 2000);
    } catch (error) {
      setCreateError(error instanceof Error ? error.message : 'Failed to create admin');
    } finally {
      setIsCreating(false);
    }
  };

  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-950 dark:to-gray-900">
        <div className="text-center">
          <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center mx-auto mb-4 animate-pulse">
            <span className="text-white font-bold text-3xl">A</span>
          </div>
          <p className="text-gray-500 dark:text-gray-400">Checking database status...</p>
        </div>
      </div>
    );
  }

  if (!needsSetup) {
    return null; // Will redirect
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-950 dark:to-gray-900 p-4">
      <div className="w-full max-w-lg">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center mx-auto mb-4">
            <span className="text-white font-bold text-3xl">A</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Welcome to Aelita</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">Set up your new instance</p>
        </div>

        {/* Success State */}
        {(restoreSuccess || createSuccess) && (
          <Card className="border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950">
            <CardBody className="text-center py-8">
              <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-green-800 dark:text-green-300 mb-2">
                {restoreSuccess ? 'Database Restored!' : 'Admin Created!'}
              </h2>
              <p className="text-green-600 dark:text-green-400">Redirecting to login...</p>
            </CardBody>
          </Card>
        )}

        {/* Setup Options */}
        {!restoreSuccess && !createSuccess && (
          <>
            {/* Tab Buttons */}
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => setActiveTab('restore')}
                className={`flex-1 py-3 px-4 rounded-lg font-medium transition-colors ${
                  activeTab === 'restore'
                    ? 'bg-primary-500 text-white'
                    : 'bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 border border-gray-200 dark:border-gray-700'
                }`}
              >
                <Upload className="h-4 w-4 inline-block mr-2" />
                Restore from Backup
              </button>
              <button
                onClick={() => setActiveTab('create')}
                className={`flex-1 py-3 px-4 rounded-lg font-medium transition-colors ${
                  activeTab === 'create'
                    ? 'bg-primary-500 text-white'
                    : 'bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 border border-gray-200 dark:border-gray-700'
                }`}
              >
                <UserPlus className="h-4 w-4 inline-block mr-2" />
                Start Fresh
              </button>
            </div>

            {/* Restore Tab */}
            {activeTab === 'restore' && (
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <Database className="h-5 w-5 text-primary-500" />
                    <h2 className="font-semibold">Restore from Backup</h2>
                  </div>
                </CardHeader>
                <CardBody className="space-y-4">
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    Upload a database backup file (.sql or .dump) from another Aelita instance to
                    restore all your data.
                  </p>

                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    accept=".sql,.dump"
                    className="hidden"
                  />

                  {!selectedFile ? (
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full p-8 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-primary-400 hover:bg-primary-50 dark:hover:bg-primary-950 transition-colors"
                    >
                      <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-600 dark:text-gray-300">Click to select a backup file</p>
                      <p className="text-xs text-gray-400 mt-1">Supports .sql and .dump files</p>
                    </button>
                  ) : (
                    <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-900 dark:text-gray-100">{selectedFile.name}</p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {(selectedFile.size / 1024).toFixed(1)} KB
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedFile(null);
                            if (fileInputRef.current) {
                              fileInputRef.current.value = '';
                            }
                          }}
                        >
                          Change
                        </Button>
                      </div>
                    </div>
                  )}

                  {restoreError && (
                    <div className="p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2">
                      <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-red-700 dark:text-red-300">{restoreError}</p>
                    </div>
                  )}

                  <Button
                    onClick={handleRestore}
                    disabled={!selectedFile}
                    isLoading={isRestoring}
                    className="w-full"
                  >
                    Restore Database
                  </Button>
                </CardBody>
              </Card>
            )}

            {/* Create Admin Tab */}
            {activeTab === 'create' && (
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <UserPlus className="h-5 w-5 text-primary-500" />
                    <h2 className="font-semibold">Create Admin Account</h2>
                  </div>
                </CardHeader>
                <CardBody>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                    Start with a fresh database by creating an administrator account.
                  </p>

                  <form onSubmit={handleCreateAdmin} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <Input
                        label="First Name"
                        value={adminForm.firstName}
                        onChange={(e) => setAdminForm({ ...adminForm, firstName: e.target.value })}
                        required
                      />
                      <Input
                        label="Last Name"
                        value={adminForm.lastName}
                        onChange={(e) => setAdminForm({ ...adminForm, lastName: e.target.value })}
                        required
                      />
                    </div>

                    <Input
                      label="Email"
                      type="email"
                      value={adminForm.email}
                      onChange={(e) => setAdminForm({ ...adminForm, email: e.target.value })}
                      placeholder="admin@company.com"
                      required
                    />

                    <Input
                      label="Password"
                      type="password"
                      value={adminForm.password}
                      onChange={(e) => setAdminForm({ ...adminForm, password: e.target.value })}
                      placeholder="Minimum 8 characters"
                      required
                    />

                    <Input
                      label="Confirm Password"
                      type="password"
                      value={adminForm.confirmPassword}
                      onChange={(e) =>
                        setAdminForm({ ...adminForm, confirmPassword: e.target.value })
                      }
                      required
                    />

                    {createError && (
                      <div className="p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2">
                        <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-red-700 dark:text-red-300">{createError}</p>
                      </div>
                    )}

                    <Button type="submit" isLoading={isCreating} className="w-full">
                      Create Admin & Continue
                    </Button>
                  </form>
                </CardBody>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
}
